import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FlaskConical, AlertTriangle, CheckCircle, HelpCircle, ShieldCheck, ChevronRight, Activity, BookOpen, Search } from 'lucide-react';

const confidenceColor = (level) => {
  if (!level) return 'bg-gray-100 text-gray-600';
  const l = level.toUpperCase();
  if (l === 'HIGH') return 'bg-green-100 text-green-700 border-green-300';
  if (l === 'MEDIUM') return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-red-100 text-red-700 border-red-300';
};

const statusIcon = (status) => {
  if (!status) return <HelpCircle className="h-3.5 w-3.5 text-gray-400" />;
  const s = status.toUpperCase();
  if (s === 'REPORTED') return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
  if (s === 'INFERRED') return <Search className="h-3.5 w-3.5 text-blue-500" />;
  if (s === 'UNCERTAIN') return <HelpCircle className="h-3.5 w-3.5 text-amber-500" />;
  return <AlertTriangle className="h-3.5 w-3.5 text-red-400" />;
};

const flagColor = (cat) => {
  if (cat === 'true_red_flag') return 'bg-red-50 border-red-200 text-red-800';
  if (cat === 'reporting_limitation') return 'bg-amber-50 border-amber-200 text-amber-800';
  return 'bg-blue-50 border-blue-200 text-blue-800';
};

const ScoreRangeDisplay = ({ tse }) => {
  if (!tse) return null;
  const { score_value, score_min, score_max, score_max_possible, confidence } = tse;
  const hasRange = score_min != null && score_max != null && score_min !== score_max;
  const pct = ((score_value || 0) / (score_max_possible || 70)) * 100;
  const pctMin = hasRange ? ((score_min || 0) / (score_max_possible || 70)) * 100 : pct;
  const pctMax = hasRange ? ((score_max || 0) / (score_max_possible || 70)) * 100 : pct;

  return (
    <div className="space-y-2" data-testid="score-range-display">
      <div className="flex items-end gap-3">
        <span className="text-3xl font-black text-slate-900 dark:text-white leading-none">
          {hasRange ? `${score_min}–${score_max}` : score_value}
        </span>
        <span className="text-sm text-slate-400 pb-0.5">/ {score_max_possible}</span>
        <Badge variant="outline" className={`ml-auto text-xs ${confidenceColor(confidence)}`}>
          {confidence || 'N/A'} confidence
        </Badge>
      </div>
      <div className="relative h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        {hasRange && (
          <div
            className="absolute h-full bg-blue-200 dark:bg-blue-800 rounded-full"
            style={{ left: `${pctMin}%`, width: `${pctMax - pctMin}%` }}
          />
        )}
        <div
          className="absolute h-full bg-blue-600 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const TypeAwareEvaluationPanel = ({ evaluation, insight }) => {
  const [expandedComponent, setExpandedComponent] = useState(null);
  if (!evaluation) return null;

  const {
    detected_study_type,
    study_type_category,
    data_source_type,
    type_specific_score,
    score_components,
    data_completeness,
    confidence_system,
    clinical_interpretation,
    red_flags,
    limitations_analysis,
    safety_disclaimer
  } = evaluation;

  const scoreLabel = type_specific_score?.score_type === 'clinical_learning'
    ? 'Clinical Learning Score'
    : type_specific_score?.score_type === 'evidence_synthesis'
      ? 'Evidence Synthesis Score'
      : 'Methodology Score';

  const categoryLabel = study_type_category === 'case_report' ? 'Case Report'
    : study_type_category === 'review' ? 'Review / Meta-analysis'
    : 'Primary Study';

  return (
    <div className="space-y-4" data-testid="type-aware-evaluation">
      {/* Header: Type + Source + Score */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-white to-blue-50 dark:from-slate-900 dark:to-blue-950/30">
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge className="bg-blue-600 text-white">{detected_study_type}</Badge>
                <Badge variant="outline" className="text-xs">{categoryLabel}</Badge>
                <Badge variant="outline" className={`text-xs ${data_source_type === 'FULL' ? 'bg-green-50 text-green-700 border-green-300' : data_source_type === 'PARTIAL' ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-red-50 text-red-700 border-red-300'}`}>
                  {data_source_type === 'FULL' ? 'Full Text' : data_source_type === 'PARTIAL' ? 'Abstract Only' : 'Citation Only'}
                </Badge>
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">{scoreLabel}</h3>
              <ScoreRangeDisplay tse={type_specific_score} />
            </div>
            {/* Clinical Interpretation */}
            {clinical_interpretation && (
              <div className="md:w-48 p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-center">
                <Activity className="h-5 w-5 mx-auto mb-1 text-slate-500" />
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Clinical Impact</p>
                <Badge className={
                  clinical_interpretation.impact_level === 'practice-changing' ? 'bg-green-600' :
                  clinical_interpretation.impact_level === 'moderate' ? 'bg-amber-600' :
                  clinical_interpretation.impact_level === 'hypothesis-generating' ? 'bg-blue-600' :
                  clinical_interpretation.impact_level === 'educational_only' ? 'bg-purple-600' : 'bg-gray-600'
                }>
                  {clinical_interpretation.impact_level?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Score Components Explainability */}
      {score_components && score_components.length > 0 && (
        <Card data-testid="score-components">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-blue-600" />
              Score Breakdown — Evidence & Reasoning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {score_components.map((comp, idx) => (
                <div key={idx} className="border border-slate-100 dark:border-slate-700 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 text-left transition-colors"
                    onClick={() => setExpandedComponent(expandedComponent === idx ? null : idx)}
                    data-testid={`score-component-${idx}`}
                  >
                    {statusIcon(comp.status)}
                    <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{comp.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-blue-700 dark:text-blue-400">{comp.value}/{comp.max}</span>
                      <Badge variant="outline" className={`text-[10px] ${confidenceColor(comp.confidence)}`}>
                        {comp.confidence}
                      </Badge>
                      <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${expandedComponent === idx ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {expandedComponent === idx && (
                    <div className="px-4 pb-3 pt-1 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 space-y-2">
                      {comp.evidence && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Evidence</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300 italic">"{comp.evidence}"</p>
                        </div>
                      )}
                      {comp.reasoning && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Reasoning</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">{comp.reasoning}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Data Status:</p>
                        <Badge variant="outline" className="text-[10px]">
                          {statusIcon(comp.status)} <span className="ml-1">{comp.status}</span>
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Score bar total */}
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Total</span>
              <span className="text-lg font-black text-blue-700 dark:text-blue-400">
                {score_components.reduce((sum, c) => sum + (c.value || 0), 0)} / {score_components.reduce((sum, c) => sum + (c.max || 0), 0)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confidence System */}
      {confidence_system && (
        <Card data-testid="confidence-system">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              Confidence Assessment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {['extraction_confidence', 'inference_confidence', 'overall_confidence'].map(key => (
                <div key={key} className="text-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">{key.replace(/_/g, ' ')}</p>
                  <Badge className={confidenceColor(confidence_system[key])}>{confidence_system[key]}</Badge>
                </div>
              ))}
            </div>
            {confidence_system.explanation && (
              <p className="text-sm text-slate-600 dark:text-slate-400 italic">{confidence_system.explanation}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Completeness */}
      {data_completeness && data_completeness.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="completeness" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="data-completeness-trigger">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold">Data Completeness Model</span>
                <Badge variant="outline" className="text-xs ml-1">
                  {data_completeness.filter(d => d.status === 'REPORTED').length}/{data_completeness.length} reported
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Element</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500">Status</th>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">Value</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500">Penalty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data_completeness.map((dc, idx) => (
                      <tr key={idx} className="border-b border-slate-50 dark:border-slate-800">
                        <td className="py-2 px-2 font-medium text-slate-700 dark:text-slate-300">{dc.element}</td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {statusIcon(dc.status)}
                            <span className="text-xs">{dc.status}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400 text-xs">{dc.extracted_value || '—'}</td>
                        <td className="py-2 px-2 text-center">
                          {dc.penalty_applied ? (
                            <span className="text-red-500 text-xs font-bold">Yes</span>
                          ) : (
                            <span className="text-green-500 text-xs">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* Red Flags */}
      {red_flags && red_flags.length > 0 && (
        <Card data-testid="red-flags">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Flags & Concerns ({red_flags.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {red_flags.map((flag, idx) => (
              <div key={idx} className={`px-3 py-2 rounded-lg border text-sm ${flagColor(flag.category)}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <Badge variant="outline" className="text-[10px]">{flag.category?.replace(/_/g, ' ')}</Badge>
                  {flag.severity && <Badge variant="outline" className="text-[10px]">{flag.severity}</Badge>}
                </div>
                <p>{flag.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Clinical Interpretation */}
      {clinical_interpretation?.explanation && (
        <Card className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800" data-testid="clinical-interpretation">
          <CardContent className="pt-4">
            <p className="text-sm text-green-900 dark:text-green-100">{clinical_interpretation.explanation}</p>
          </CardContent>
        </Card>
      )}

      {/* Safety Disclaimer */}
      {safety_disclaimer && (
        <p className="text-xs text-slate-400 dark:text-slate-500 italic text-center mt-2" data-testid="safety-disclaimer">
          {safety_disclaimer}
        </p>
      )}
    </div>
  );
};

export default TypeAwareEvaluationPanel;
